'use client';

import { useCallback } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { getBaseProperties, setCheckoutSessionId, clearCheckoutSessionId } from './base-properties';
import { isAmplitudeInitialized, getAmplitudeDeviceId, getAmplitudeSessionId } from './init';
import type { AmplitudeEvents, AmplitudeEventName, AmplitudeUserProperties } from './types';
import config from '@/constants/config';

export function useAmplitude() {
  const track = useCallback(
    <T extends AmplitudeEventName>(eventName: T, properties: AmplitudeEvents[T]): void => {
      if (!isAmplitudeInitialized()) {
        console.warn(`[Amplitude] Not initialized, skipping event: ${eventName}`);
        return;
      }

      try {
        const baseProps = getBaseProperties();
        const eventProps = { ...baseProps, ...properties };

        amplitude.track(eventName, eventProps);

        if (config.environment === 'development') {
          console.log(`[Amplitude] ${eventName}`, eventProps);
        }
      } catch (error) {
        console.error(`[Amplitude] Error tracking ${eventName}:`, error);
      }
    },
    [],
  );

  const identify = useCallback((userId: string, userProps?: AmplitudeUserProperties): void => {
    if (!isAmplitudeInitialized()) {
      console.warn('[Amplitude] Not initialized, skipping identify');
      return;
    }

    try {
      amplitude.setUserId(userId);

      if (userProps) {
        const identifyObj = new amplitude.Identify();
        Object.entries(userProps).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            identifyObj.set(key, value);
          }
        });
        amplitude.identify(identifyObj);
      }

      if (config.environment === 'development') {
        console.log(`[Amplitude] Identified user: ${userId}`, userProps);
      }
    } catch (error) {
      console.error('[Amplitude] Error identifying user:', error);
    }
  }, []);

  const reset = useCallback((): void => {
    if (!isAmplitudeInitialized()) return;

    try {
      amplitude.reset();
      if (config.environment === 'development') {
        console.log('[Amplitude] User reset');
      }
    } catch (error) {
      console.error('[Amplitude] Error resetting user:', error);
    }
  }, []);

  const setUserProperty = useCallback((key: string, value: string | number | boolean): void => {
    if (!isAmplitudeInitialized()) return;

    try {
      const identifyObj = new amplitude.Identify();
      identifyObj.set(key, value);
      amplitude.identify(identifyObj);
    } catch (error) {
      console.error('[Amplitude] Error setting user property:', error);
    }
  }, []);

  const getDeviceId = useCallback((): string | undefined => {
    return getAmplitudeDeviceId();
  }, []);

  const getSessionId = useCallback((): number | undefined => {
    return getAmplitudeSessionId();
  }, []);

  return {
    track,
    identify,
    reset,
    setUserProperty,
    getDeviceId,
    getSessionId,
    setCheckoutSessionId,
    clearCheckoutSessionId,
  };
}

export function trackAmplitudeEvent<T extends AmplitudeEventName>(
  eventName: T,
  properties: AmplitudeEvents[T],
): void {
  if (!isAmplitudeInitialized()) {
    console.warn(`[Amplitude] Not initialized, skipping event: ${eventName}`);
    return;
  }

  try {
    const baseProps = getBaseProperties();
    const eventProps = { ...baseProps, ...properties };

    amplitude.track(eventName, eventProps);

    if (config.environment === 'development') {
      console.log(`[Amplitude] ${eventName}`, eventProps);
    }
  } catch (error) {
    console.error(`[Amplitude] Error tracking ${eventName}:`, error);
  }
}
